import React, { useState } from 'react';
import Alert, { AlertColor } from '@mui/material/Alert';
import { useLocation } from 'react-router-dom';
import useBundleTranslation from 'i18n';
import { domToReact, renderHtml, renderShowdown } from 'tools/safeHtml';
import qs from 'query-string';

import isSnakeCase from 'tools/isSnakeCase';
import GridComponent from 'components/common/grid/GridComponent';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import IconHandler, { IconType } from 'components/common/icon/IconHandler';
import RouteLink from 'components/common/route-link/RouteLink';
import LightTooltip from 'components/common/tooltip/LightTooltip';
import { Box, Chip, ChipProps, Link, Stack } from '@mui/material';

interface IProps {
    type?: AlertColor;
    icon?: IconType | boolean;
    align?: string;
    label: string;
    show?: boolean;
    translationNS?: string;
    link?: {
        label?: string;
        href?: string;
    };
    variant?: any;
    translationParams?: {
        [key: string]: string | number;
    };
    gridUrl?: string;
    popupConfig?: PopupSettings;
    planeText?: boolean;
    useMarkdown?: boolean;
    /**
     * Pre-translated hover content for the message: a plain string (newline-separated lines
     * render as rows) or a badge row (tag chips) followed by text lines.
     */
    tooltip?: string | StaticInfoTooltip;
}

export interface StaticInfoTooltip {
    badges?: { label: string; color?: ChipProps['color'] }[];
    lines?: string[];
}

type Params = {
    [key: string]: any;
};

const GRID_URL_PREFIX = 'gridURL';

const renderTooltipTitle = (tooltip?: string | StaticInfoTooltip): React.ReactNode => {
    if (typeof tooltip === 'string') {
        const text = tooltip.trim();
        return text ? (
            <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                {text}
            </Box>
        ) : null;
    }
    const badges = tooltip?.badges ?? [];
    const lines = tooltip?.lines ?? [];
    if (!badges.length && !lines.length) {
        return null;
    }
    return (
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
            {badges.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {badges.map((badge, i) => (
                        <Chip
                            key={`${badge.label}-${i}`}
                            variant="tag"
                            color={badge.color ?? 'default'}
                            label={badge.label}
                            data-test="static-info-tooltip-badge"
                        />
                    ))}
                </Stack>
            )}
            {lines.map((line, i) => (
                <Box key={`${line}-${i}`} component="span">
                    {line}
                </Box>
            ))}
        </Stack>
    );
};

const StaticInfo: React.FC<IProps> = ({
    icon,
    type = 'info',
    label,
    align,
    translationNS,
    link,
    show = true,
    variant = 'standard',
    translationParams = {},
    gridUrl,
    popupConfig = {},
    planeText = false,
    useMarkdown = true,
    tooltip,
}) => {
    const trimLabel = label?.trim();
    const location = useLocation();
    const shouldTranslate = isSnakeCase(trimLabel);
    const [activeGridUrl, setActiveGridUrl] = useState('');
    const [isGridPopupOpen, toggleGridPopupOpen] = useState(false);
    const [urlParams, setUrlParams] = useState<Params | null>(null);
    const { t } = useBundleTranslation(translationNS ?? 'components/common/form/form');
    const text = shouldTranslate ? t(trimLabel, { ...translationParams }) : trimLabel;
    const html = useMarkdown ? renderShowdown(text) : text;
    const currentLink = location.pathname + location.hash;
    let iconElement = icon === false ? false : icon ? <IconHandler icon={icon} /> : undefined;
    if (variant === 'blank' && typeof icon == 'undefined') {
        //remove default icon for blank variant
        iconElement = false;
    }

    const openGridPopup = (parseUrl: string) => {
        const { url, query } = qs.parseUrl(parseUrl);
        setUrlParams(query);
        setActiveGridUrl(url);
        toggleGridPopupOpen(true);
    };

    const closeGridPopup = () => {
        setActiveGridUrl('');
        toggleGridPopupOpen(false);
    };

    const options: any = {
        replace: ({ attribs, children }: any) => {
            if (!attribs) {
                return;
            }

            if (attribs?.href?.includes(GRID_URL_PREFIX) && gridUrl) {
                return (
                    <Link href={currentLink} onClick={() => openGridPopup(gridUrl)}>
                        {domToReact(children, options)}
                    </Link>
                );
            }
        },
    };
    // Every label is markup (translated sentences with names in them, server messages): cleaned by the one module.
    const reactElement = renderHtml(html, options);

    const popupSettings: PopupSettings = {
        noCancel: true,
        title: urlParams?.title || '',
        textOK: urlParams?.textOK || t('close'),
        ...popupConfig,
    };

    const action = link?.href && link?.label ? <RouteLink to={link.href}>{link.label}</RouteLink> : null;

    const message = planeText ? trimLabel : reactElement;
    const tooltipTitle = renderTooltipTitle(tooltip);
    const content = tooltipTitle ? (
        <LightTooltip title={tooltipTitle} placement="top-start">
            <Box component="span" tabIndex={0} sx={{ cursor: 'help' }} data-test="static-info-tooltip-target">
                {message}
            </Box>
        </LightTooltip>
    ) : (
        message
    );

    return show ? (
        <>
            <Alert
                action={action}
                severity={type}
                icon={iconElement}
                variant={variant}
                sx={{
                    justifyContent: align == 'center' ? 'center' : undefined,
                    '& .MuiAlert-action': { alignItems: 'center', paddingTop: 0, marginRight: 0 },
                }}
            >
                {content}
            </Alert>
            {isGridPopupOpen && activeGridUrl && (
                <Popup
                    open
                    key="static-info-list"
                    translationNS={translationNS}
                    settings={popupSettings}
                    onHide={closeGridPopup}
                >
                    <GridComponent t={t} uid={activeGridUrl} settingsUrl={activeGridUrl} />
                </Popup>
            )}
        </>
    ) : null;
};

export default StaticInfo;
